import { useLoaderData } from "@tanstack/react-router";
import { Plus, Search } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { PageHeader } from "@/components/shared/page-header";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { UserCreateDialog } from "./user-create-dialog";
import { UserTable } from "./user-table";

export interface UserRecord {
  id: string;
  name: string;
  email: string;
  role: string | null;
  banned: boolean | null;
  banReason: string | null;
  createdAt: string;
  updatedAt: string;
  emailVerified: boolean;
  image: string | null;
}

export function UsersPage() {
  const loaderData = useLoaderData({ from: "/_authenticated/users" as any }) as
    | { users: UserRecord[]; total: number }
    | undefined;

  const [users, setUsers] = useState<UserRecord[]>(loaderData?.users ?? []);
  const [total, setTotal] = useState(loaderData?.total ?? 0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [hasInteracted, setHasInteracted] = useState(false);

  const fetchUsers = useCallback(
    async (signal?: AbortSignal) => {
      setLoading(true);
      try {
        const params = new URLSearchParams({
          page: page.toString(),
          limit: "20",
        });
        if (search) params.set("search", search);

        const res = await fetch(`/api/users?${params}`, { signal });
        if (!res.ok) throw new Error("Failed to fetch");
        const data = await res.json();
        setUsers(data.users);
        setTotal(data.total);
      } catch (e) {
        if (e instanceof DOMException && e.name === "AbortError") return;
        console.error("Failed to fetch users:", e);
      } finally {
        setLoading(false);
      }
    },
    [page, search],
  );

  useEffect(() => {
    if (hasInteracted) {
      const controller = new AbortController();
      fetchUsers(controller.signal);
      return () => controller.abort();
    }
  }, [fetchUsers, hasInteracted]);

  const handleSearchChange = (value: string) => {
    setSearch(value);
    setPage(1);
    setHasInteracted(true);
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
    setHasInteracted(true);
  };

  const handleRefresh = () => {
    setHasInteracted(true);
    fetchUsers();
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="User Management"
        description="Manage users, roles, and access control."
        actions={
          <Button onClick={() => setCreateOpen(true)}>
            <Plus className="mr-2 h-4 w-4" />
            Add User
          </Button>
        }
      />

      <div className="flex items-center gap-2">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search users..."
            value={search}
            onChange={(e) => handleSearchChange(e.target.value)}
            className="pl-9"
          />
        </div>
      </div>

      <UserTable
        users={users}
        loading={loading}
        total={total}
        page={page}
        onPageChange={handlePageChange}
        onRefresh={handleRefresh}
      />

      <UserCreateDialog open={createOpen} onOpenChange={setCreateOpen} onCreated={handleRefresh} />
    </div>
  );
}
